import React from 'react'
import { Badge, Button, makeStyles, tokens } from '@fluentui/react-components'
import { GridItem } from '../common/grid/GridItem'
import { ControlledDatePicker } from '../common/inputs/DatePicker'
import { ControlledTextInput } from '../common/inputs/TextInput'
import { ControlledColorPicker } from '../common/inputs/ControlledColorPicker'
import { ControlledAccountPicker } from '../common/inputs/ControlledAccountPicker'
import { ModalActions } from '../common/modal/Modal'
import { UseFormReturn } from 'react-hook-form'
import { Activity, ActivityStatus } from '../../api/schemas/schema'
import { t } from '../../i18n/i18n'
import { CommentThread } from './CommentThread'

const useStyles = makeStyles({
   marginLeft: {
      '@media screen and (min-width: 641px)': {
         marginLeft: tokens.spacingHorizontalL,
      },
   },
   calendarWheelView: {
      '@media screen and (min-width: 641px)': {
         display: 'flex',
         flexDirection: 'row-reverse',
      },
   },
   phoneBtn: {
      '@media screen and (max-width: 640px)': {
         width: '100%',
         display: 'flex',
         marginBottom: '5px',
      },
   },
   titleRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacingHorizontalM,
   },
   titleInput: {
      flex: 1,
      minWidth: 0,
   },
})

type ActivityFormProps = {
   activityForm: UseFormReturn<Activity, any, Activity>
   modalState: {
      isOpen: boolean
      selectedActivity?: Activity
   }

   onClose: () => void
}

export const ActivityForm = ({ activityForm, modalState, onClose }: ActivityFormProps) => {
   const classes = useStyles()
   const selectedActivity = modalState?.selectedActivity
   const {
      control,
      getValues,
      watch,
      setValue,
      formState: { isDirty, isSubmitting },
   } = activityForm

   return (
      <>
         {selectedActivity?.status === ActivityStatus.Completed && (
            <GridItem size="12/12">
               {/* Read-only on purpose - the status is only ever changed from the three-dots menu, so
                   there is one way in and one confirmation. */}
               <Badge appearance="tint" color="informative">
                  {t('Completed')}
               </Badge>
            </GridItem>
         )}
         <GridItem size="12/12">
            <div className={classes.titleRow}>
               <div className={classes.titleInput}>
                  <ControlledTextInput control={control} label={t('Title')} name={'title'} required />
               </div>
               <ControlledColorPicker control={control} name={'colorHex'} label={t('Color')} />
            </div>
         </GridItem>
         <GridItem size="12/12">
            <ControlledTextInput control={control} label={t('Description')} name={'description'} multiline />
         </GridItem>
         <GridItem size="6/12">
            <ControlledDatePicker required control={control} name={'startDate'} label={t('StartDate')} />
         </GridItem>
         <GridItem size="6/12">
            <ControlledDatePicker required control={control} name={'endDate'} label={t('EndDate')} getValues={getValues} compareWith={'startDate'} oneDayActivity />
         </GridItem>
         <GridItem size="12/12">
            <ControlledAccountPicker label={t('Responsible')} control={control} name={'responsible'} watch={watch} setValue={setValue} />
         </GridItem>
         {selectedActivity?.id && (
            <GridItem size="12/12">
               <CommentThread activityId={selectedActivity.id} />
            </GridItem>
         )}
         <GridItem size="12/12">
            <ModalActions>
               <div className={classes.calendarWheelView}>
                  <Button
                  disabled={isSubmitting}
                     className={classes.phoneBtn}
                     onClick={() => {
                        onClose()
                     }}
                  >
                     {t('Cancel')}
                  </Button>
               </div>
               <Button type="submit" appearance="primary" disabled={!isDirty || isSubmitting}>
                  {t(selectedActivity ? 'Update' : 'Save')}
               </Button>
            </ModalActions>
         </GridItem>
      </>
   )
}
